import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@nesso/ui/primitives/card';

import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { ProgressBar } from '@/shared/ui/indicators/progress-bar';

type PlanBillingPlanCardProps = {
    sessionsUsed: number;
    sessionsIncluded: number;
    overageRateCents: number;
    onManagePlan: () => void;
    manageDisabled?: boolean;
    // Set where the billing summary card owns the Manage Plan control, so it isn't offered twice.
    manageHidden?: boolean;
    isError?: boolean;
    isDisabled?: boolean;
};

export function PlanBillingPlanCard({
    sessionsUsed,
    sessionsIncluded,
    overageRateCents,
    onManagePlan,
    manageDisabled = false,
    manageHidden = false,
    isError = false,
    isDisabled = false,
}: PlanBillingPlanCardProps) {
    const percent =
        sessionsIncluded > 0
            ? Math.min(100, Math.round((sessionsUsed / sessionsIncluded) * 100))
            : 0;
    const isExceeded = sessionsUsed > sessionsIncluded;

    return (
        <Card className={cn('shadow-none mb-8', isDisabled && 'opacity-50')}>
            <CardHeader>
                <CardTitle className="text-base font-bold">Current plan</CardTitle>
                {!manageHidden && (
                    <CardAction>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onManagePlan}
                            disabled={manageDisabled || isDisabled}
                        >
                            Manage Plan
                        </Button>
                    </CardAction>
                )}
                {!isError && (
                    <div className="flex flex-col items-start gap-1 @sm/card-header:flex-row @sm/card-header:items-end @sm/card-header:gap-2">
                        <span className="text-3xl font-semibold">{sessionsUsed}</span>
                        <span className="text-sm text-muted-foreground">
                            / {sessionsIncluded} sessions
                        </span>
                    </div>
                )}
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                {isError ? (
                    <p className="text-sm text-muted-foreground">
                        We can't load your plan information right now. Try again later.
                    </p>
                ) : (
                    <>
                        <ProgressBar value={percent} />
                        {isExceeded && (
                            <p className="text-sm text-muted-foreground">
                                <span className="font-bold">
                                    More clients, more impact—you've exceeded your plan!
                                </span>{' '}
                                Extra sessions are just {formatCurrencyFromCents(overageRateCents)}{' '}
                                and will be added to next month's bill.
                            </p>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
